import {
  useDashboard,
  useIntercom,
  useSubscription,
  useTeam,
} from '@vrplatform/data';
import { LoadingIndicator } from '@vrplatform/components-legacy';
import type { Maybe } from '@vrplatform/utils';
import { Anchor, Text } from '@mantine/core';
import {
  type ComponentProps,
  type ReactNode,
  Suspense,
  useEffect,
} from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { usePropertyManagementConnectionsQuery } from '@vrplatform/data';
import { lazyWithRetry } from '../../routes/dashboard/_utils';
import {
  Layout,
  OnboardingSidebar,
  type OnboardingStep,
  OnboardingTeamSwitch,
} from './components';
import { OnboardingMenu } from './components/OnboardingMenu';

const OnboardingConnectRoute = lazyWithRetry(
  () => import('./routes/OnboardingConnectRoute')
);
const OnboardingPartnerAgreementRoute = lazyWithRetry(
  () => import('./routes/OnboardingPartnerAgreementRoute')
);
const OnboardingPartnerCallRoute = lazyWithRetry(
  () => import('./routes/OnboardingPartnerCallRoute')
);
const OnboardingPricingRoute = lazyWithRetry(
  () => import('./routes/OnboardingPricingRoute')
);
const OnboardingSettingsRoute = lazyWithRetry(
  () => import('./routes/OnboardingSettingsRoute')
);

const SettingsView = lazyWithRetry(() => import('../../views/settings'));

export const OnboardingRoutes = () => {
  const [
    {
      id: teamId,
      subscription: { status: contextSubscriptionStatus },
      isGL: GL,
    },
    refetchTeam,
  ] = useTeam();
  const [dashboard] = useDashboard();
  const location = useLocation();

  const { data: propertyManagementSystems = [] } =
    usePropertyManagementConnectionsQuery({ limit: 1 });

  const hasPms = propertyManagementSystems.length > 0;

  const { data: subscription } = useSubscription(
    (q, args) => {
      const tenant = q.tenantById({
        id: args.teamId,
      });

      return {
        status: tenant?.billingSubscriptionStatus as Maybe<
          typeof contextSubscriptionStatus
        >,
      };
    },
    {
      teamId,
    },
    {
      skip: !teamId,
    }
  );

  const billingStatus = subscription?.status || contextSubscriptionStatus;

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (subscription?.status) {
      refetchTeam();
    }
  }, [subscription?.status]);

  if (GL || dashboard === 'partner')
    return (
      <Routes>
        {/* connection oauth */}
        <Route
          path="/callback"
          element={
            <Navigate
              to={`/connect-pms${location.search || ''}&callback=true`}
            />
          }
        />

        {/* settings */}
        <Route
          path="/settings/*"
          element={
            <Suspense fallback={<LoadingIndicator isFullPageLoading />}>
              <SettingsView />
            </Suspense>
          }
        />

        {/* onboarding routes */}
        <Route element={<OnboardingLayout />}>
          <Route path="*" element={<SuspendedRoutes />} />
        </Route>
      </Routes>
    );

  return (
    <Routes>
      {/* settings */}
      <Route path="/settings/*" element={<SettingsView />} />

      <Route element={<OnboardingLayout />}>
        {!billingStatus ? (
          <>
            <Route path="/trial" element={<OnboardingPricingRoute />} />
            <Route path="/*" element={<Navigate to="/trial" replace />} />
          </>
        ) : (
          <>
            <Route
              path="/connect-pms"
              element={<OnboardingConnectRoute type="PMS" />}
            />
            <Route
              path="/connect-accounting"
              element={<OnboardingConnectRoute type="Accounting" />}
            />

            <Route
              path="/*"
              element={
                <Navigate
                  to={
                    hasPms
                      ? `/connect-accounting${location.search}`
                      : `/connect-pms${location.search}`
                  }
                />
              }
            />
          </>
        )}
      </Route>
      <Route
        path="/callback"
        element={
          <Navigate to={`/connect-pms${location.search || ''}&callback=true`} />
        }
      />
    </Routes>
  );
};

const SuspendedRoutes = () => {
  const [dashboard] = useDashboard();
  const { data: propertyManagementSystems = [] } =
    usePropertyManagementConnectionsQuery({ limit: 1 });

  const hasPms = propertyManagementSystems.length > 0;

  return (
    <Suspense fallback={<LoadingIndicator isFullPageLoading />}>
      <Routes>
        {/* onboarding routes */}
        {dashboard === 'partner' ? (
          <>
            <Route path="/book-call" element={<OnboardingPartnerCallRoute />} />
            <Route
              path="/partner-agreement"
              element={<OnboardingPartnerAgreementRoute />}
            />
            <Route path="/*" element={<Navigate to={'/book-call'} replace />} />
          </>
        ) : (
          <>
            <Route
              path="/connect-pms"
              element={
                hasPms ? (
                  <Navigate to="/start-date" replace />
                ) : (
                  <OnboardingConnectRoute type="PMS" />
                )
              }
            />
            <Route path="/start-date" element={<OnboardingSettingsRoute />} />
            {/* <Route
                path="/select-listing"
                element={<OnboardingSelectListingRoute />}
              /> */}
            <Route
              path="/*"
              element={
                <Navigate
                  to={
                    hasPms
                      ? `/start-date${location.search}`
                      : `/connect-pms${location.search}`
                  }
                  replace
                />
              }
            />
          </>
        )}
      </Routes>
    </Suspense>
  );
};

type SidebarProps = Pick<
  ComponentProps<typeof Layout>,
  'sidebar' | 'title' | 'description'
>;

const OnboardingLayout = () => {
  const location = useLocation();
  const { show: showIntercom } = useIntercom();
  const [{ isGL: GL }] = useTeam();
  const [dashboard] = useDashboard();

  type OnboardingPath =
    | '/trial'
    | '/start-date'
    | '/select-listing'
    | '/connect-pms'
    | '/connect-accounting'
    | '/partner-agreement'
    | '/book-call';

  const side: Record<OnboardingPath, SidebarProps> = {
    '/trial': {
      title: '14-Day Free Trial with VRPlatform',
      description:
        "Try our accounting automation and owner statement tools with no risk and no commitment. We won't charge you until after your free 14 days, and you can cancel anytime.",
      sidebar: <OnboardingSidebar type="pricing" />,
    },
    '/start-date': {
      title: 'Account Settings',
      description:
        'Select your country and the month to start generating owner statements. You can pick a past date, and billing will begin then.',
      sidebar: <OnboardingSidebar type="settings" />,
    },
    '/select-listing': {
      title: 'Select a listing for your product tour',
      description:
        'Do you want to see how your owner statement would look? Select a listing, and we will show you the process.',
      sidebar: null,
    },
    '/connect-pms': {
      title: 'Connect your PMS',
      description:
        'We will collect, clean, and organize your data so you can gain valuable insights and take action based on your findings.',
      sidebar: null,
    },
    '/connect-accounting': {
      title: 'Connect your Accounting',
      description:
        'We will collect, clean, and organize your data so you can gain valuable insights and take action based on your findings.',
      sidebar: null,
    },
    '/book-call': {
      title: 'Book 30-minute onboarding',
      description:
        "We'll guide you through account setup and key platform features. Get personalized support to ensure a smooth start.",
      sidebar: null,
    },
    '/partner-agreement': {
      title: 'Review & Accept Partner Agreement',
      description: 'Thank you for joining us as an accounting partner!',
      sidebar: null,
    },
  };

  const steps: Record<OnboardingPath, OnboardingStep | undefined> = {
    '/connect-accounting': 'connect-accounting',
    '/connect-pms': 'connect-pms',
    '/select-listing': 'select-listing',
    '/start-date': 'start-date',
    '/trial': 'add-billing',
    '/book-call': 'book-call',
    '/partner-agreement': 'partner-agreement',
  };

  const footers: Record<OnboardingPath, ReactNode> = {
    '/connect-accounting': (
      <Text c="gray" ta="center">
        Having trouble connecting your accounting software?{' '}
        <Anchor inline component="button" onClick={showIntercom}>
          Contact onboarding team
        </Anchor>
      </Text>
    ),
    '/connect-pms': (
      <Text c="gray" ta="center">
        Having trouble connecting your PMS?{' '}
        <Anchor inline component="button" onClick={showIntercom}>
          Contact onboarding team
        </Anchor>
      </Text>
    ),
    '/start-date': (
      <Text c="gray" ta="center">
        Need history data? No problem contact our{' '}
        <Anchor inline component="button" onClick={showIntercom}>
          onboarding team
        </Anchor>
      </Text>
    ),

    '/select-listing': (
      <Text c="gray" ta="center">
        Not sure what listing to select? Contact our{' '}
        <Anchor inline component="button" onClick={showIntercom}>
          onboarding team
        </Anchor>
      </Text>
    ),
    '/trial': (
      <Text c="gray" ta="center">
        Do you need a help? Don’t hesitate to{' '}
        <Anchor inline component="button" onClick={showIntercom}>
          contact our onboarding team
        </Anchor>
      </Text>
    ),
    '/book-call': null,
    '/partner-agreement': (
      <Text c="gray" ta="center">
        Got questions?{' '}
        <Anchor inline component="button" onClick={showIntercom}>
          Contact our onboarding team
        </Anchor>
      </Text>
    ),
  };

  const path = location.pathname as OnboardingPath;

  return (
    <Layout
      teamSelectComponent={<OnboardingTeamSwitch />}
      footer={footers[path]}
      activeStep={steps[path]}
      menu={<OnboardingMenu />}
      GL={GL ? 'no-billing' : undefined}
      teamType={dashboard === 'partner' ? 'partner' : 'propertyManager'}
      {...side[path]}
    >
      <Outlet />
    </Layout>
  );
};
